;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { ReadonlyURLSearchParams } from 'next/navigation';
import { Locale } from '../i18n/i18n-config';
import axios from 'axios';
import axiosInstance from '../utils/axios';
import { logApiError } from '../utils/logApiError';
import { logSentryError } from '../utils/logSentry';
import isValidId from '../utils/isValidId';
import { filterValidFacetValues, toValidPage } from '../utils/jobSearchParams';

const app = process.env.NEXT_PUBLIC_APP;

export const getJobs = async ({ limit = 100000 }: { limit: number }): Promise<unknown> => {
  const { data } = await axiosInstance.get(`/jobs`, { params: { app, limit } });
  return data;
};

export type FacetCount = { id: string; name: string; count?: number };
export const facetGroupNames = [
  'contractTypes',
  'division',
  'experiences',
  'jobDomains',
  'laborRegimes',
  'locations',
  'shifts',
  'workPlaceType',
  'student',
  'where',
  'what',
  'radius',
  'page',
  'sort',
] as const;

export type FacetGroupName = (typeof facetGroupNames)[number];
export type JobSearchFacets = Partial<Record<FacetGroupName, FacetCount[]>>;

export interface JobsSearchResponse {
  count: number;
  fromHits: number;
  maxHits: number;
  matches: Job[];
  facets: JobSearchFacets;
}

export type JobSearchQuery = {
  what?: string;
  where?: string;
  radius?: string;
  page?: string;
  language: string;
  jobDomains?: string[];
  contractTypes?: string[];
  laborRegimes?: string[];
  experiences?: string[];
  student?: boolean;
  sort?: boolean | string;
  locations?: string[];
  agency?: string | number;
  divisions?: string[];
  shifts?: string[];
  workPlaceTypes?: string[];
};

export const convertSearchParamsToJobSearchQuery = (
  params: NextPageSearchParams | ReadonlyURLSearchParams,
  locale: Locale,
  options?: { exclude?: FacetGroupName[] }
): JobSearchQuery => {
  let searchParams: { [key: string]: string | string[] | undefined };

  if (params instanceof URLSearchParams) searchParams = Object.fromEntries(params.entries());
  else searchParams = params;

  const convertToArrayVal = (val): string[] => {
    if (typeof val === 'string') return [val];
    else if (Array.isArray(val)) return val;
    return [];
  };

  const jobSearchQuery: JobSearchQuery = { language: locale };

  const page = toValidPage(searchParams.page);
  if (page && !options?.exclude?.includes('page')) jobSearchQuery.page = String(page);

  facetGroupNames
    .filter((fg) => (options?.exclude ? options.exclude.indexOf(fg) === -1 : true))
    .forEach((facetGroup) => {
      const param = searchParams[facetGroup];
      if (!param) return;
      switch (facetGroup) {
        case 'student':
          jobSearchQuery.student = param === 'true';
          break;
        case 'locations':
          let queryVal: string[] = [];
          if (Array.isArray(param)) queryVal = param.map((p) => p.replace(/!/g, '#'));
          else queryVal = convertToArrayVal(param.replace(/!/g, '#'));
          jobSearchQuery.locations = queryVal;
          break;
        case 'where':
        case 'what':
        case 'radius':
          if (typeof param === 'string') jobSearchQuery[facetGroup] = param.replace('#', ' ');
          break;
        case 'division':
          jobSearchQuery.divisions = convertToArrayVal(param);
          break;
        case 'workPlaceType':
          jobSearchQuery.workPlaceTypes = convertToArrayVal(param);
          break;
        case 'contractTypes':
        case 'experiences':
        case 'jobDomains':
        case 'laborRegimes':
          jobSearchQuery[facetGroup] = filterValidFacetValues(facetGroup, convertToArrayVal(param));
          break;
        case 'shifts':
          jobSearchQuery[facetGroup] = convertToArrayVal(param);
          break;
        default:
          break;
      }
    });

  return jobSearchQuery;
};

// @INFO: return a list of all active job facets
export const getActiveFacetsFromSearchParams = (
  searchParams: ReadonlyURLSearchParams,
  options?: { exclude?: FacetGroupName[] }
) => {
  const activeFacets: Partial<Record<FacetGroupName, string[]>> = {};
  facetGroupNames
    .filter((fg) => (options?.exclude ? options.exclude.indexOf(fg) === -1 : true))
    .forEach((param) => {
      const paramValues = searchParams?.getAll(param);
      if (paramValues.length > 0 && paramValues.every((p) => !!p)) activeFacets[param] = paramValues;
    });

  return activeFacets;
};

export const getJobData = async ({
  what = '',
  where = '',
  radius = '25',
  page = '1',
  language,
  jobDomains = [],
  contractTypes = [],
  laborRegimes = [],
  experiences = [],
  student = false,
  sort = false,
  locations = [],
  agency,
  divisions = [],
  shifts = [],
  workPlaceTypes = [],
}: JobSearchQuery): Promise<JobsSearchResponse> => {
  const sortDetails = sort === 'updateDate' ? { property: 'updated', order: 'desc' } : false;

  try {
    const { data } = await axiosInstance.post<JobsSearchResponse>(`/jobs/search`, {
      app: agency ? 'synergiejobs.be' : app,
      language,
      jobDomains,
      contractTypes,
      laborRegimes,
      experiences,
      student,
      sort: sortDetails,
      locations,
      agency,
      search: what,
      fromHits: (parseInt(page) - 1) * 10 || 0,
      maxHits: 10,
      where,
      radius: parseInt(radius) || 25,
      divisions,
      shifts,
      workPlaceTypes,
    });

    // @INFO: use following object to exclude facetGroups for certain Apps
    const excludedFacets: Record<App, FacetGroupName[]> = {
      'synergiejobs.be': ['division'],
      'werkenbijsynergie.be': ['jobDomains', 'contractTypes', 'laborRegimes', 'experiences', 'shifts', 'workPlaceType'],
      'travaillerchezsynergie.be': ['jobDomains', 'contractTypes', 'laborRegimes', 'experiences', 'shifts', 'workPlaceType'],
      'sandyou.be': ['division'],
      'synergiecareers.be': ['division'],
      'synergieconstruct.be': ['division'],
    };

    Object.entries(data.facets)
      .filter(([facetGroup, facet]) => {
        const facetToReturn =
          !Object.values(facet).every(({ count }) => count === 0) && // exclude facets with count 0
          app &&
          excludedFacets[app].indexOf(facetGroup as FacetGroupName) === -1 && // exclude facets from 'excludedFacets' config
          !(facetGroup === 'locations' && ['pl', 'ro'].includes(language)); // exclude location facet for PL & RO
        return !facetToReturn;
      })
      .forEach(([facetToDelete]) => delete data.facets[facetToDelete]);

    return data;
  } catch (error) {
    // 422 ZodError: malformed query params (typically from crawlers hitting
    // URLs like /jobs?jobDomains=foo?jobDomains=foo). Return an empty result
    // so the page renders cleanly instead of letting Next.js dump the full
    // Axios error to stderr while it builds the 500 page.
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      logApiError(error, 'getJobData');
      return { count: 0, fromHits: 0, maxHits: 10, matches: [], facets: {} };
    }
    logSentryError(error);
    throw error;
  }
};

export const getJobsForCompany = async ({
  company,
  language,
  page,
}: {
  company: number | string;
  language: string;
  page: number | string;
}): Promise<JobsResponse> => {
  try {
    const { data } = await axiosInstance.get(`/jobs`, {
      params: { company, language, page: toValidPage(page), app: process.env.NEXT_PUBLIC_APP },
    });
    return data;
  } catch (error) {
    logSentryError(error);
    return { jobs: [], count: 0, totalPages: 0, currentPage: 1 };
  }
};

export const getJob = async (id: string | string[], includeUnpublished = false): Promise<Job | null> => {
  if (!isValidId(id)) return null;
  try {
    const { data } = await axiosInstance.get(`/jobs/${id}`, {
      params: { includeUnpublished },
    });
    return data;
  } catch (error) {
    if (error.response?.status === 404) logApiError(error, 'getJob');
    else logSentryError(error);

    return null;
  }
};
